import { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '@/components/ui/Card';
import { Section, useBearer } from './_admin-utils';

interface DashboardMetrics {
  queue_oldest_hours?: number
  avg_first_response_hours?: number
  auto_resolve_pct?: number
  sla_compliance_pct?: number
  overdue_count?: number
  numa_assist_rate?: number
  backlog_by_status?: Record<string, number>
  backlog_by_priority?: Record<string, number>
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (num >= 24) return `${Math.round(num)}h`;
  return `${num.toFixed(1)}h`;
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${num.toFixed(1)}%`;
}

export default function AdminOpsDashboard() {
  const bearer = useBearer();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mock, setMock] = useState(false);

  const loadMetrics = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError('');
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/admin/ops-dashboard`, {
        headers: { ...bearer },
      });
      setMock(response.headers.get('x-mock-data') === 'true');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load metrics');
      }
      setMetrics(payload?.metrics || null);
    } catch (err) {
      setMetrics(null);
      setError((err as Error)?.message || 'Unable to load metrics');
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const backlogByStatus = useMemo(() => {
    const entries = metrics?.backlog_by_status || {};
    return Object.entries(entries).sort((a, b) => Number(b[1]) - Number(a[1]));
  }, [metrics?.backlog_by_status]);

  const backlogByPriority = useMemo(() => {
    const entries = metrics?.backlog_by_priority || {};
    return Object.entries(entries).sort((a, b) => Number(b[1]) - Number(a[1]));
  }, [metrics?.backlog_by_priority]);

  if (!bearer) {
    return (
      <Section title="Support Ops Dashboard">
        <p className="text-sm text-cocoa/70">Sign in as an admin to view the dashboard.</p>
      </Section>
    );
  }

  return (
    <Section
      title="Support Ops Dashboard"
      actions={
        <button
          type="button"
          onClick={loadMetrics}
          className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      }
    >
      {mock ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Supabase not configured – displaying mock support metrics.
        </div>
      ) : null}
      {error ? <p className="mb-4 text-sm text-brick">{error}</p> : null}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">Oldest queue ticket</p>
          <p className="mt-2 text-2xl font-semibold text-cocoa">{formatHours(metrics?.queue_oldest_hours)}</p>
        </Card>
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">Avg. first response (30d)</p>
          <p className="mt-2 text-2xl font-semibold text-cocoa">{formatHours(metrics?.avg_first_response_hours)}</p>
        </Card>
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">Auto-resolve rate</p>
          <p className="mt-2 text-2xl font-semibold text-cocoa">{formatPercent(metrics?.auto_resolve_pct)}</p>
        </Card>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">SLA compliance</p>
          <p className="mt-2 text-2xl font-semibold text-cocoa">{formatPercent(metrics?.sla_compliance_pct)}</p>
          <p className="mt-1 text-xs text-cocoa/60">{Number(metrics?.overdue_count || 0)} overdue tickets</p>
        </Card>
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">NUMA assist rate</p>
          <p className="mt-2 text-2xl font-semibold text-cocoa">{formatPercent(metrics?.numa_assist_rate)}</p>
          <p className="mt-1 text-xs text-cocoa/60">Share quick wins with the automation team.</p>
        </Card>
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">Ops alerts</p>
          <ul className="mt-2 list-disc pl-4 text-xs">
            <li>Escalate any ticket past next_action_due_at.</li>
            <li>Prioritize NUMA confidence under 50%.</li>
            <li>Document credit adjustments in the ledger.</li>
          </ul>
        </Card>
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">Backlog by status</p>
          <ul className="mt-3 space-y-2">
            {backlogByStatus.length ? (
              backlogByStatus.map(([label, count]) => (
                <li key={label} className="flex items-center justify-between">
                  <span className="font-semibold text-cocoa">{label}</span>
                  <span className="text-cocoa/70">{Number(count)}</span>
                </li>
              ))
            ) : (
              <li className="text-xs text-cocoa/60">No data available.</li>
            )}
          </ul>
        </Card>
        <Card className="p-4 text-sm text-cocoa/80">
          <p className="text-xs uppercase tracking-wide text-cocoa/50">Backlog by priority</p>
          <ul className="mt-3 space-y-2">
            {backlogByPriority.length ? (
              backlogByPriority.map(([label, count]) => (
                <li key={label} className="flex items-center justify-between">
                  <span className="font-semibold capitalize text-cocoa">{label}</span>
                  <span className="text-cocoa/70">{Number(count)}</span>
                </li>
              ))
            ) : (
              <li className="text-xs text-cocoa/60">No data available.</li>
            )}
          </ul>
        </Card>
      </div>
      <Card className="mt-6 space-y-3 bg-shell/40 p-5 text-sm text-cocoa">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-cocoa">Ops runbook: Triage in 2 minutes</h3>
          <a
            href="https://github.com/slurm-github/project-tower-website/blob/work/docs/runbooks/admin-triage.md"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold uppercase tracking-wide text-cocoa underline"
          >
            View full runbook
          </a>
        </div>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-cocoa/80">
          <li>Check SLA panel and claim any overdue ticket.</li>
          <li>Scan NUMA summary and confidence chip for context.</li>
          <li>Review credit snapshot metadata if the ticket is tagged as credits.</li>
          <li>Send an initial response or escalate before the two-minute mark.</li>
          <li>Log any adjustments using the ticket-credit-adjust tool.</li>
        </ol>
        <p className="text-xs text-cocoa/60">
          See <code>docs/runbooks/admin-triage.md</code> for detailed escalation flows.
        </p>
      </Card>
    </Section>
  );
}
